import React, { useState, useRef, useEffect } from 'react';
import {
  useNavigate,
  useSearchParams,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import MainSearch from './MainSearch/MainSearch.jsx';
import DatePickerButton from '../../../stories/general-components/DatePickerButton.jsx';
import RangePickerButton from '../../../stories/general-components/RangePickerButton.jsx';
import * as Icons from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Divider,
  Menu,
  MenuItem,
  IconButton,
  Avatar,
  Typography,
  Tabs,
  Badge,
  Switch,
  Tab,
  Toolbar,
  ButtonBase,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  AvatarGroup,
  Select,
  FormControl,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import nodeAxiosFirebase from '../../../utils/nodeAxiosFirebase.js'
import DialogWindow from '../../../stories/general-components/DialogWindow.jsx';
import { setGeneralStatus } from '../../../redux/actions-v2/coreAction.js';
import TagSelectorMenu from '../../../stories/general-components/TagSelectorMenu.jsx';


const AnimatedButton = ({ icon, title, onClick, disabled, children, isDarkMode }) => {
  const [isHovered, setIsHovered] = useState(false);
  const IconComponent = Icons[icon] || Icons.Error;

  return (
    <motion.div
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      style={{
        display: 'inline-block',
        margin: '0 6px'
      }}
    >
      <motion.div
        animate={{
          width: isHovered ? 'auto' : '42px',
          borderRadius: isHovered ? '21px' : '50%',
          padding: isHovered ? '0px' : '0',
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: isDarkMode ? '#00000090' : '#FFFFFF95',
          height: '42px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          position: 'relative',
        }}
      >
        <div style={{
          width: '42px',
          height: '42px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <IconButton
            size="large"
            edge="start"
            color="black"
            onClick={onClick}
            disabled={disabled}
            sx={{
              padding: 0,
              margin: 0,
              height: '42px',
              minHeight: '42px',
              width: '42px',
              maxHeight: '42px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': {
                position: 'relative',
                zIndex: 2,
                fontSize: '20px',
                margin: 0,
                padding: 0,
              },
            }}
          >
            <IconComponent
              color={isDarkMode ? 'white' : 'black'}
              style={{
                margin: 0,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </IconButton>
        </div>
        <motion.div
          initial={{ opacity: 0, width: 0 }}
          animate={{
            opacity: isHovered ? 1 : 0,
            width: isHovered ? 'auto' : 0,
            marginLeft: isHovered ? 6 : 0,
            marginRight: isHovered ? 6 : 0,
          }}
          style={{
            color: isDarkMode ? 'white' : 'black',
            fontSize: '10px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {title}
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

const TopNavBar = ({
  isTablet,
  pageTitle,
  actions,
  isMobile,
  iconEmoji,
  searchActivated,
  elementId,
  formatedPath,
  iconMain,
  manualIndex,
  usersInRoom,
  tabs,
  sectionTitle,
  subTitle,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { pathname: currentPath } = useLocation();
  const longPressTimeout = useRef(null);
  const { moduleName, moduleId, structureId } = useParams();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [isHovered, setIsHovered] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const businessStructure = useSelector(
    (state) => state.core.businessStructure
  );

  const modules = businessStructure?.modules || [];
  const activeModule = modules?.find((m) => m.id === moduleId);
  const localStorageStart = localStorage.getItem(activeModule?.id + 'start');
  const localStorageEnd = localStorage.getItem(activeModule?.id + 'end');

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElSelect, setAnchorElSelect] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [longPress, setLongPress] = useState(false);
  const [tagsValue, setTagsValue] = useState(actions?.tags);
  const [dialogOpenCells, setDialogOpenCells] = useState(false);
  const [dateRange, setDateRange] = useState([
    activeModule?.list?.preferences?.customizations?.backToToday
      ? moment().startOf('day')?.format('YYYY-MM-DD')
      : localStorageStart
        ? moment.unix(localStorageStart)?.format('YYYY-MM-DD')
        : moment().startOf('day').format('YYYY-MM-DD'),
    activeModule?.list?.preferences?.customizations?.backToToday
      ? moment().endOf('day')?.format('YYYY-MM-DD')
      : localStorageEnd
        ? moment.unix(localStorageEnd)?.format('YYYY-MM-DD')
        : moment().endOf('day').format('YYYY-MM-DD'),
  ]);
  const [currentSelect, setCurrentSelect] = useState();
  const [openSelect, setOpenSelect] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [layoutMenuAnchor, setLayoutMenuAnchor] = useState(null);

  const handleDateRangeChange = (newDateRange) => {
    setSearchParams(searchParams);
    if (actions?.onChangeDateRange?.rangeDates === 'single') {
      setDateRange([newDateRange[0], newDateRange[0]]);
      searchParams.set('startDate', newDateRange[0]?.format('YYYY-MM-DD'));
      searchParams.set('endDate', newDateRange[1]?.format('YYYY-MM-DD'));
      actions?.onChangeDateRange?.action &&
        actions?.onChangeDateRange.action([newDateRange[0], newDateRange[0]]);
    } else if (actions?.onChangeDateRange?.rangeDates !== 'none') {
      setDateRange(newDateRange);
      searchParams.set('startDate', newDateRange[0]?.format('YYYY-MM-DD'));
      searchParams.set('endDate', newDateRange[1]?.format('YYYY-MM-DD'));
      actions?.onChangeDateRange?.action &&
        actions?.onChangeDateRange.action(newDateRange);
    }
  };

  const [activeIndex, setActiveIndex] = useState(0);

  const InnerShadowIconButton = React.forwardRef(
    ({ icon, onClick, disabled, noMargin, transparent, ...props }, ref) => {
      const IconComponent = Icons[icon] || Icons.Error;

      return (
        <span>
          <IconButton
            ref={ref}
            {...props}
            size="large"
            edge="start"
            color="black"
            onClick={typeof onClick === 'function' ? onClick : undefined}
            disabled={disabled}
            sx={{
              ml: noMargin ? 0 : 1,
              backgroundColor: transparent
                ? 'transparent'
                : isDarkMode
                  ? '#00000090'
                  : '#FFFFFF95',
              padding: '0px',
              height: '42px',
              minHeight: '42px',
              width: '42px',
              maxHeight: '42px',
              position: 'relative',
              '& svg': {
                position: 'relative',
                zIndex: 2,
              },
            }}
          >
            <IconComponent color={isDarkMode ? 'white' : 'black'} />
          </IconButton>
        </span>
      );
    }
  );

  const updateTabIndexInURL = (index) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('tab', index);
    navigate(`${pathname}?${searchParams.toString()}`);
  };

  const handleTabChange = (event, id) => {
    setActiveIndex(id);
    updateTabIndexInURL(id);
  };

  const businessPreference = useSelector((state) => state.core.businessData);
  const employees = businessPreference?.employees;
  const currentUser = useSelector((state) => state.core.user);

  const activeStructure = businessStructure?.structures?.find(
    (structure) => structure.id === structureId
  );
  const businessModules = businessStructure?.modules;

  const singleElementDetails = useSelector(
    (state) => state.element.singleElementDetails
  );

  useEffect(() => {
    if (singleElementDetails?.tags?.length > 0 && elementId) {
      setTagsValue(singleElementDetails?.tags);
    }
  }, [singleElementDetails?.id, elementId]);

  const handleTags = (event) => {
    setClicked(!clicked);
  };

  const handleClose = () => {
    setClicked(false);
    setOpen(false);
  };

  const handleTagsChange = async (event, newValue) => {
    setTagsValue(newValue);
    try {
      await nodeAxiosFirebase({
        t,
        method: 'PATCH',
        url: `coreSeqV4`,
        body: {
          documentId: elementId,
          elementPath: formatedPath || 'cards',
          key: 'tags',
          value: newValue,
        },
      });
    } catch (error) {
      console.error('Error set tags');
      dispatch(setGeneralStatus({ status: 'error', error: error }));
    }
  };

  const module = businessModules?.find((m) => m.id === moduleId);

  const getModuleName = () => {
    if (pageTitle) {
      return pageTitle;
    }

    return module?.name;
  };

  const history = localStorage.getItem('history')
    ? JSON.parse(localStorage.getItem('history'))
    : [];

  const filtredHistory = history.filter(
    (loc) => loc.businessId === businessPreference?.id
  );

  const navigateBack = () => {
    const history = localStorage.getItem('history')
      ? JSON.parse(localStorage.getItem('history'))
      : [];
    const lastElementMinusOne = history[history?.length - 2];
    navigate(lastElementMinusOne?.url || lastElementMinusOne?.pathname);
  };

  const handleSelect = (item) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('selection', item?.value);
    navigate(`${pathname}?${searchParams.toString()}`);
    setCurrentSelect(item);
    setAnchorElSelect(null);
    setOpenSelect(false);
  };

  const handleCloseSelect = () => {
    setAnchorElSelect(null);
    setOpenSelect(false);
  };

  const handleOpenSelect = () => {
    setAnchorElSelect(anchorElSelect);
    setOpenSelect(true);
  };

  const cellsFormatted = actions?.cells?.[0]?.ids?.map((cell) => {
    const cellId = cell.toString();
    let id;
    let name;

    if (cell.toString().endsWith('mondaycom')) {
      id = cellId.split('mondaycom')[0];
      name = 'mondaycom';
    } else if (cell.toString().endsWith('trello')) {
      id = cellId.split('trello')[0];
      name = 'trello';
    } else if (cell.toString().endsWith('quickbooks')) {
      id = cellId.split('quickbooks')[0];
      name = 'quickbooks';
    } else if (cell.toString().endsWith('vln')) {
      id = cellId.split('vln')[0];
      name = 'vln';
    } else {
      id = cellId;
    }

    return {
      id: id,
      name: name,
    };
  });

  const handleButtonPress = (e) => {
    setLongPress(false);
    longPressTimeout.current = setTimeout(() => {
      setLongPress(true);
      setMenuAnchor(e.target);
    }, 750);
  };

  const handleButtonRelease = () => {
    clearTimeout(longPressTimeout.current);
    if (!longPress) {
      navigateBack();
    }
  };

  const openCells = () => {
    setDialogOpenCells(true);
  };

  const handleCellSelect = (cell) => { };

  const IconComponent = module?.icon
    ? Icons[module?.icon]
    : activeStructure?.icon
      ? Icons[activeStructure?.icon]
      : Icons[iconMain]
        ? Icons[iconMain]
        : null

  const handleLayoutMenuOpen = (event) => {
    setLayoutMenuAnchor(event.currentTarget);
  };

  const handleLayoutMenuClose = () => {
    setLayoutMenuAnchor(null);
  };

  const handleLayoutChange = (mode) => {
    if (actions?.editLayout?.publicAccessAction) {
      actions.editLayout.publicAccessAction();
    }
    handleLayoutMenuClose();
  };

  return (
    <div
      style={{
        position: 'relative',
        height: '55px',
        maxHeight: '55px',
        paddingBottom: '20px',
        minHeight: '55px',
      }}
    >
      <DialogWindow
        title={t('cells')}
        open={dialogOpenCells}
        size={'medium'}
        width={'medium'}
        onClose={() => setDialogOpenCells(false)}
      >
        <div>
          <List>
            {cellsFormatted &&
              cellsFormatted?.map((cell, idx) => (
                <ListItem
                  key={idx}
                  divider
                  onClick={() => handleCellSelect(cell)}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{ width: '30px', height: '30px' }}
                      src={`/assets/v3/connectors/${cell?.name}.png`}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={cell?.id}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      color: isDarkMode ? '#FFF' : '#000',
                    }}
                  />
                </ListItem>
              ))}
          </List>
        </div>
      </DialogWindow>
      <Toolbar
        sx={{
          height: '45px',
          minHeight: '45px',
          maxHeight: '45px',
          borderRadius: '12px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignContent: 'left',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
          flex={isMobile ? 0.5 : searchActivated ? 7 : 4}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-start',
              width: isMobile ? '30px' : '100%',
              marginRight: '40px',
              maxWidth: isMobile ? '30px' : searchActivated ? '200px' : '23vh',
              minWidth: isMobile ? '30px' : searchActivated ? '200px' : '23vh',
            }}
            flex={isMobile ? 0.2 : searchActivated ? 4 : 2}
          >
            <div style={{ marginTop: '5px' }}>
              <span
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="d-flex middle-content"
              >
                {' '}
                <div>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    elevation={3}
                    sx={{
                      marginTop: '18px',
                      marginLeft: '-14px',
                      '& .MuiPaper-root': {
                        borderRadius: '12px',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 200,
                          overflowY: 'auto',
                          borderRadius: '12px',
                        },
                      },
                    }}
                  >
                    {filtredHistory?.map((loc, index) => (
                      <MenuItem
                        divider
                        dense
                        key={index}
                        onClick={() => navigate(loc?.pathname)}
                      >
                        <div>
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            fontSize="12px"
                            style={{
                              color: isDarkMode ? '#FFFFFF' : '#000000',
                            }}
                          >
                            {loc?.name}
                          </Typography>

                          <Typography
                            variant="body1"
                            fontWeight={400}
                            fontSize="11px"
                            style={{
                              color: isDarkMode ? '#ffffff60' : '#00000060',
                            }}
                          >
                            {loc?.moduleName}
                          </Typography>
                        </div>
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
                {(isHovered || isMobile) ? (
                  <Tooltip title={t('navigateBack')}>
                    <IconButton
                      sx={{
                        width: '25px',
                        height: '25px',
                        maxHeight: '25px',
                        maxWidth: '25px',
                        minWidth: '25px',
                        borderRadius: '50%',
                        padding: '0px',
                      }}
                      size="medium"
                      onMouseDown={handleButtonPress}
                      onMouseUp={handleButtonRelease}
                      onTouchStart={handleButtonPress}
                      onTouchEnd={handleButtonRelease}
                    >
                      <Icons.ArrowBack
                        sx={{
                          fontSize: '20px',
                        }}
                        htmlColor={isDarkMode ? '#ffffff' : '#000000'}
                      />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <>
                    <IconButton
                      sx={{
                        width: '25px',
                        height: '25px',
                        maxHeight: '25px',
                        maxWidth: '25px',
                        minWidth: '25px',
                        borderRadius: '50%',
                        padding: '0px',
                      }}
                      size="medium"
                    >
                      {iconMain?.startsWith('https') ? <img src={iconMain} alt="icon" style={{ width: '25px', height: '25px' }} /> : (iconMain || module?.icon || activeStructure?.icon) ? (
                        <IconComponent
                          sx={{
                            fontSize: '16px',
                          }}
                          htmlColor={isDarkMode ? '#ffffff' : '#000000'}
                        />) : (<div>{iconEmoji}</div>)}
                    </IconButton>
                  </>
                )}
                {!isMobile && (
                  <Tooltip title={getModuleName(pageTitle, currentLanguage)}>
                    <Typography
                      sx={{
                        maxWidth: '23vh',
                        overflow: 'hidden',
                        marginLeft: '4px',
                        textOverflow: 'ellipsis',
                        color: isDarkMode ? '#FFF' : '#000',
                        whiteSpace: 'nowrap',
                      }}
                      fontSize={isTablet ? '16px' : '18px'}
                      fontWeight={600}
                    >
                      {actions?.editLayout?.display
                        ? t('editMode')
                        : getModuleName(pageTitle, currentLanguage)}
                    </Typography>
                  </Tooltip>
                )}
              </span>
              {!isMobile && (
                <div className="d-flex" style={{ marginTop: '-5px' }}>
                  <div>
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: 'grey',
                        marginTop: '4.5px',
                        whiteSpace: 'nowrap',
                      }}
                      fontSize={isTablet ? '10px' : '11px'}
                      fontWeight={300}
                    >
                      {(activeStructure?.name || subTitle || pageTitle || '') +
                        (sectionTitle ? ' - ' + (sectionTitle || '') : '')}
                    </Typography>
                  </div>
                </div>)}
            </div>
          </Box>
          {!currentPath?.startsWith('/bos/element') && searchActivated && (
            <motion.div
              initial={{ width: searchActivated ? '20vh' : '0vh' }}
              animate={{
                width: clicked ? '30vh' : searchActivated ? '20vh' : '0vh',
              }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  minWidth: clicked ? '30vh' : searchActivated ? '20vh' : '0vh',
                }}
                flex={clicked ? 7 : 4}
              >
                <div style={{ marginTop: isMobile ? '10px' : '', marginLeft: isMobile ? '-35px' : '' }}>
                  <MainSearch
                    userLogged={true}
                    userType="business"
                    isDarkMode={isDarkMode}
                    structureCollection={activeStructure?.collectionField}
                    setClicked={setClicked}
                    clicked={clicked}
                  />
                </div>
              </Box>
            </motion.div>
          )}
          <motion.div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              width: '100%',
            }}
            initial={{ width: '30vh' }}
            animate={{ width: clicked ? '20vh' : '30vh' }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
                width: '100%',
              }}
              flex={clicked ? 3 : 6}
            >
              {usersInRoom && (
                <div className="mx-4">
                  <AvatarGroup
                    style={{ display: 'flex', gap: '-10px' }}
                    max={4}
                    total={usersInRoom?.length}
                  >
                    {usersInRoom?.length > 0 &&
                      usersInRoom?.map((userId) => {
                        const userDetails = employees?.find(
                          (employee) => employee?.id === userId
                        );

                        return (
                          <div key={userId} style={{ position: 'relative' }}>
                            <Tooltip title={userDetails?.displayName}>
                              <Avatar
                                style={{ marginLeft: '-15px' }}
                                alt={userDetails?.displayName}
                                src={userDetails?.avatar}
                              />
                            </Tooltip>
                          </div>
                        );
                      })}
                  </AvatarGroup>
                </div>
              )}
              {actions?.cells && !clicked && (
                <AnimatedButton
                  icon="HubOutlined"
                  title={t('cells')}
                  onClick={openCells}
                  disabled={false}
                  isDarkMode={isDarkMode}
                />
              )}
              {actions?.share && !clicked && !actions?.editLayout?.display && (
                <AnimatedButton
                  icon="LinkOutlined"
                  title={t('sharableLink')}
                  onClick={actions?.share}
                  disabled={actions?.share === 'disabled'}
                />
              )}
              {!isMobile && actions?.refresh && !actions?.editLayout?.display && (
                <AnimatedButton
                  icon="LoopOutlined"
                  title={t('refresh')}
                  onClick={actions?.refresh}
                  disabled={actions?.refresh === 'disabled'}
                />
              )}
              {!isMobile && actions?.filters && actions?.setFilters && !actions?.editLayout?.display && (
                <AnimatedButton
                  icon="TuneOutlined"
                  title={t('filters')}
                  onClick={actions?.setFilters === 'disabled' ? null : actions?.setFilters}
                  disabled={actions?.setFilters === 'disabled'}
                />
              )}
              {actions?.add && !actions?.editLayout?.display && !clicked && (
                <AnimatedButton
                  icon="Add"
                  title={t('create')}
                  onClick={actions?.add === 'disabled' ? null : actions?.add}
                  disabled={actions?.add === 'disabled'}
                />
              )}
              {actions?.edit && !actions?.editLayout?.display && !clicked && (
                <AnimatedButton
                  icon="EditOutlined"
                  title={t('edit')}
                  onClick={actions?.edit === 'disabled' ? null : actions?.edit}
                  disabled={actions?.edit === 'disabled'}
                />
              )}
              {actions?.save && !actions?.editLayout?.display && !clicked && (
                <AnimatedButton
                  icon="SaveOutlined"
                  title={t('save')}
                  onClick={actions?.save === 'disabled' ? null : actions?.save}
                  disabled={actions?.save === 'disabled'}
                />
              )}
              {actions?.editLayout?.display &&
                (actions?.editLayout?.addBlock ||
                  actions?.editLayout?.addBlockPublic) &&
                !clicked && (
                  <AnimatedButton
                    icon="AddBoxOutlined"
                    title={t('addBlock')}
                    onClick={actions?.editLayout?.addBlock === 'disabled' ? null :
                      actions?.editLayout?.displayPublic
                        ? actions?.editLayout?.addBlockPublic
                        : actions?.editLayout?.addBlock
                    }
                    disabled={actions?.editLayout?.addBlock === 'disabled'}
                  />
                )}

              <div
                className="d-flex middle-content"
                style={{
                  backgroundColor: isDarkMode ? '#00000090' : '#FFFFFF95',
                  borderRadius: '30px',
                  marginTop: isMobile ? '8px' : '',
                  marginLeft: isMobile ? '-65px' : '8px',
                }}
              >
                {actions?.previous && !actions?.editLayout?.display && (
                  <InnerShadowIconButton
                    noMargin
                    transparent
                    icon="ArrowBackIos"
                    onClick={actions?.previous}
                    disabled={
                      actions?.previous === 'disabled' ||
                      actions?.currentPage === 1
                    }
                  />
                )}
                {actions?.currentPage &&
                  !actions?.editLayout?.display &&
                  actions?.next && (
                    <Typography
                      variant="subtitle1"
                      component="div"
                      fontWeight={600}
                    >
                      {actions?.currentPage}
                    </Typography>
                  )}
                {actions?.next && !actions?.editLayout?.display && (
                  <InnerShadowIconButton
                    icon="ArrowForwardIos"
                    onClick={actions?.next}
                    transparent
                    disabled={actions?.next === 'disabled'}
                  />
                )}
              </div>
              {!isMobile && actions?.deleteItem && !actions?.editLayout?.display && (
                <>
                  {(currentUser?.activeBusiness?.role === 'SUPER-ADMIN' ||
                    currentUser?.activeBusiness?.role === 'ADMIN') && (
                      <AnimatedButton
                        icon="DeleteOutlineOutlined"
                        title={t('delete')}
                        onClick={actions?.deleteItem === 'disabled' ? null : actions?.deleteItem}
                        disabled={actions?.deleteItem === 'disabled'}
                        isDarkMode={isDarkMode}
                      />
                    )}
                </>
              )}
              {actions?.select && (
                <div id="select">
                  <ButtonBase
                    sx={{
                      backgroundColor: 'success',
                      color: 'black',
                      fontWeight: 500,
                      paddingLeft: '10px',
                      paddingRight: '10px',
                      paddingTop: '3px',
                      fontSize: '13.5px',
                    }}
                    onClick={handleOpenSelect}
                  >
                    {currentSelect?.label || t('select')}
                  </ButtonBase>

                  <Menu
                    anchorEl={anchorElSelect}
                    open={openSelect}
                    onClose={handleCloseSelect}
                    PaperProps={{
                      style: {
                        maxHeight: 400,
                        width: 200,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 200,
                          overflowY: 'auto',
                          borderRadius: '12px',
                        },
                      },
                    }}
                  >
                    {actions?.select?.map((item, idx) => (
                      <MenuItem key={idx} onClick={() => handleSelect(item)}>
                        {item?.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
              )}
              {!isMobile && actions?.email && !actions?.editLayout?.display && (
                <AnimatedButton
                  icon="EmailOutlined"
                  title={t('sendEmail')}
                  onClick={actions?.email === 'disabled' ? null : actions?.email}
                  disabled={actions?.email === 'disabled'}
                />
              )}
              {!isMobile && actions?.print && !actions?.editLayout?.display && (
                <AnimatedButton
                  icon="LocalPrintshopOutlined"
                  title={t('print')}
                  onClick={actions?.print === 'disabled' ? null : actions?.print}
                  disabled={actions?.print === 'disabled'}
                />
              )}

              {!isMobile && actions?.export && !actions?.editLayout?.display && (
                <AnimatedButton
                  icon="FileDownloadOutlined"
                  title={t('export')}
                  onClick={actions?.export === 'disabled' ? null : actions?.export}
                  disabled={actions?.export === 'disabled'}
                />
              )}
              {actions?.convert?.action && !actions?.editLayout?.display && (
                <AnimatedButton
                  icon="SwitchAccessShortcutOutlined"
                  title={t('convert')}
                  onClick={actions?.convert?.action === 'disabled' ? null : actions?.convert?.action}
                  disabled={actions?.convert?.action === 'disabled'}
                />
              )}
              {!isMobile && elementId && !actions?.editLayout?.display && (
                <motion.div
                  initial={{ width: '15vh' }} // Initial width
                  animate={{ width: clicked ? '270px' : '15vh' }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                  className="d-flex middle-content"
                >
                  <>
                    <AnimatedButton
                      icon={
                        actions?.tags?.length > 0
                          ? 'LocalOffer'
                          : 'LocalOfferOutlined'
                      }
                      title={t('manageTags')}
                      onClick={anchorEl ? handleClose : handleTags}
                    />
                  </>

                  {clicked && (
                    <div
                      style={{
                        width: clicked ? '250px' : '0px',
                        marginRight: '10px',
                      }}
                    >
                      <TagSelectorMenu
                        key={elementId}
                        fullWidth
                        error={null}
                        required={false}
                        label={t('tags')}
                        value={tagsValue}
                        fieldType={'tags'}
                        onChange={handleTagsChange}
                      />
                    </div>
                  )}
                </motion.div>
              )}

              <Divider
                component="div"
                orientation="vertical"
                flexItem
                sx={{
                  height: '30px',
                  marginTop: '12px',
                  ml: 1,
                }}
              />
              {!isMobile && actions?.editLayout &&
                currentUser?.activeBusiness?.role === 'SUPER-ADMIN' && (
                  <>
                    <AnimatedButton
                      icon={
                        actions?.editLayout?.display
                          ? 'SaveOutlined'
                          : 'ViewQuiltOutlined'
                      }
                      title={
                        actions?.editLayout?.display
                          ? t('save')
                          : t('editLayout')
                      }
                      onClick={
                        actions?.editLayout?.displayPublic
                          ? actions?.editLayout?.actionPublic
                          : actions?.editLayout?.action
                      }
                    />
                  </>
                )}
              {!isMobile && actions?.editLayout?.publicAccessAction &&
                !actions?.editLayout?.display && (
                  <>
                    <ButtonBase
                      onClick={handleLayoutMenuOpen}
                      sx={{
                        backgroundColor: isDarkMode ? '#00000090' : '#FFFFFF95',
                        borderRadius: '21px',
                        height: '42px',
                        padding: '0 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginLeft: '8px',
                      }}
                    >
                      <Icons.PreviewOutlined
                        sx={{
                          fontSize: '20px',
                          color: isDarkMode ? 'white' : 'black',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '10px',
                          fontWeight: 500,
                          color: isDarkMode ? 'white' : 'black',
                        }}
                      >
                        {actions?.editLayout?.publicAccess ? t('publicLayout') : t('adminLayout')}
                      </Typography>
                      <Icons.ArrowDropDown
                        sx={{
                          fontSize: '20px',
                          color: isDarkMode ? 'white' : 'black',
                        }}
                      />
                    </ButtonBase>
                    <Menu
                      anchorEl={layoutMenuAnchor}
                      open={Boolean(layoutMenuAnchor)}
                      onClose={handleLayoutMenuClose}
                      PaperProps={{
                        sx: {
                          borderRadius: '12px',
                          mt: 1,
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => handleLayoutChange('admin')}
                        selected={!actions?.editLayout?.publicAccess}
                        sx={{
                          minWidth: '150px',
                          fontSize: '14px',
                        }}
                      >
                        <Icons.AdminPanelSettingsOutlined sx={{ mr: 1 }} />
                        {t('adminLayout')}
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleLayoutChange('public')}
                        selected={actions?.editLayout?.publicAccess}
                        sx={{
                          minWidth: '150px',
                          fontSize: '14px',
                        }}
                      >
                        <Icons.PublicOutlined sx={{ mr: 1 }} />
                        {t('publicLayout')}
                      </MenuItem>
                    </Menu>
                  </>
                )}
              {!isMobile && actions?.settings &&
                (currentUser?.activeBusiness?.role === 'SUPER-ADMIN' ||
                  currentUser?.activeBusiness?.role === 'ADMIN') && (
                  <>
                    <AnimatedButton
                      icon="SettingsOutlined"
                      title={t('pageParams')}
                      onClick={actions?.settings}
                    />
                  </>
                )}
              {actions?.date && !actions?.editLayout?.display && (
                <div style={{ marginLeft: isMobile ? '-10px' : '8px', marginTop: isMobile ? '10px' : '' }}>
                  <DatePickerButton
                    formatDensity="dense"
                    value={actions?.date}
                    onChange={actions?.onChangeDate}
                  />
                </div>
              )}
              {actions?.onChangeDateRange && !actions?.editLayout?.display && (
                <div style={{ marginLeft: isMobile ? '-10px' : '8px', marginTop: isMobile ? '10px' : '' }}>
                  <RangePickerButton
                    formatDensity="dense"
                    value={dateRange}
                    type={actions?.onChangeDateRange?.rangeDates}
                    onChange={handleDateRangeChange}
                  />
                </div>
              )}
            </Box>
          </motion.div>

          {actions?.approve && !actions?.editLayout?.display && (
            <ButtonBase
              sx={{
                borderRadius: '10px',
                backgroundColor: 'success',
                color: 'black',
                fontWeight: 500,
                fontSize: '12px',
              }}
              onClick={actions?.approve}
            >
              {t('sendApproval')}
            </ButtonBase>
          )}
        </Box>

        <Box
          sx={{
            alignItems: 'right',
            display: 'flex',
            justifyContent: 'flex-end',
            // marginLeft: isMobile ? '-85px' : '',
            // marginTop: isMobile ? '6px' : '',
            // maxWidth: isMobile ? '140px' : ''
          }}
          flex={isMobile ? 1 : 2}
        >
          <Tabs
            sx={{
              height: '42px',
              maxHeight: '42px',
              minHeight: '42px',
              backgroundColor: isDarkMode ? '#000' : '#FFF',
              borderRadius: '40px',
              position: 'relative',
              overflow: 'hidden',
              '.MuiTabs-indicator': {
                display: 'none',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                height: '100%',
                width: '100%',
                borderRadius: '4px',
                transition: 'transform 0.3s ease-in-out',
                zIndex: 1,
                transform: `translateX(${(manualIndex ?? activeIndex) * 100}%)`,
              },
            }}
            variant="scrollable"
            scrollButtons="auto"
            value={manualIndex ?? activeIndex}
            onChange={actions?.editLayout?.display ? null : handleTabChange}
          >
            {tabs &&
              tabs
                ?.filter((tab) => tab?.label || tab?.name)
                ?.map((tab, index) => {
                  return (
                    <Tab
                      label={tab?.label || tab?.name}
                      key={'tab' + tab?.label || tab?.name}
                      style={{
                        fontWeight:
                          (manualIndex ?? activeIndex) === index
                            ? '600'
                            : '500',
                        textTransform: 'capitalize',
                        padding: '10px 20px 10px 20px',
                        fontSize: '13px',
                      }}
                      sx={{
                        position: 'relative',
                        zIndex: 2,
                        minHeight: '42px',
                        maxHeight: '42px',
                        height: '42px',
                        color: isDarkMode
                          ? '#FFFFFF !important'
                          : (manualIndex ?? activeIndex) !== index
                            ? '#000000 !important'
                            : '#FFFFFF !important',
                        backgroundColor:
                          (manualIndex ?? activeIndex) === index
                            ? businessPreference?.mainColor
                            : 'transparent',
                        minWidth: isMobile ? '30px' : '',
                        borderRadius: '30px',
                        transition: 'color 0.3s ease-in-out',
                        '&:hover': {
                          color: '#FFFFFF',
                        },
                      }}
                    />
                  );
                })}
          </Tabs>
          {!isMobile && (actions?.editLayout?.addTab || actions?.editLayout?.addTabPublic) && (currentUser?.activeBusiness?.role === 'SUPER-ADMIN') && (
            <Tab
              label={'+'}
              key={'tab-add'}
              onClick={actions?.editLayout?.addTabPublic ? actions?.editLayout?.addTabPublic : actions?.editLayout?.addTab}
              style={{
                fontWeight:
                  '500',
                textTransform: 'capitalize',
                padding: '10px 20px 10px 20px',
                fontSize: '13px',
              }}
              sx={{
                position: 'relative',
                zIndex: 2,
                minHeight: '42px',
                maxHeight: '42px',

                height: '42px',
                color: isDarkMode
                  ? '#FFFFFF !important' :
                  '#000000 !important',

                backgroundColor:
                  'transparent',
                minWidth: isMobile ? '30px' : '',
                borderRadius: '30px',
                transition: 'color 0.3s ease-in-out',
                '&:hover': {
                  color: '#FFFFFF',
                },
              }}
            />
          )}
        </Box>
        {actions?.editLayout?.display && (
          <>
            <AnimatedButton
              icon="Add"
              title={t('addTab')}
              onClick={
                actions?.editLayout?.displayPublic
                  ? actions?.editLayout?.addTabPublic
                  : actions?.editLayout?.addTab
              }
            />
          </>
        )}
      </Toolbar>
    </div>
  );
};

export default TopNavBar;
